import {
  useCheckExamStatusByCardIdLazyQuery,
  useStartExam,
  StartExamErrorCode,
} from '@exam-room/graphql';
import { useTranslation } from '@exam-room/translations';
import * as Sentry from '@sentry/react';
import { useDisclosure } from '@shared/hooks';
import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { Button, Card, LoadingSpinner } from '../../../components';
import { useAuth, useExamCardExpired, useExamEvent, useServerTime } from '../../../providers';
import { PATHS } from '../../../routes';

import { ExamGuidelines } from './components';
import StartExamErrorModal from './components/StartExamErrorModal';
import { ExamDetails } from './containers';

export function Instruction() {
  const navigate = useNavigate();
  const { examCardId } = useParams();
  const { user } = useAuth();
  const [checked, setChecked] = useState(false);
  const { closestExam } = useExamEvent();
  const { timeOffset } = useServerTime();
  const [showErrorModal, { open, close }] = useDisclosure(false);
  const [startExamErrorVariant, setStartExamErrorVariant] = useState<
    'not-in-exam-time' | 'system-error'
  >('system-error');
  const [loading, setLoading] = useState(false);

  const [startedTimeHasReached, setStartedTimeHasReached] = useState(false);
  const { showExamCardExpiredModal } = useExamCardExpired();

  const { t } = useTranslation();

  const [startExam] = useStartExam({
    variables: {
      input: {
        examCardId: examCardId ?? '',
        isSetTimer: true,
      },
    },
  });

  const [checkExamStatusByCardId] = useCheckExamStatusByCardIdLazyQuery({
    variables: {
      examCardId: examCardId ?? '',
    },
    fetchPolicy: 'network-only',
  });

  const handleStartExam = async () => {
    setLoading(true);
    try {
      const examStatus = await checkExamStatusByCardId();
      if (examStatus.data?.erCheckExamStatusByExamCardId?.id) {
        navigate(generatePath(PATHS.Exam.Root, { examCardId }));
        return;
      }

      const startExamResult = await startExam();
      const isSuccess = startExamResult.data?.erStartExamInEvent?.success;

      if (isSuccess) {
        navigate(generatePath(PATHS.Exam.Root, { examCardId }));
        return;
      }

      const errorCode = startExamResult.data?.erStartExamInEvent?.errorCode;
      if (errorCode === StartExamErrorCode.ExpiredExamCard) {
        showExamCardExpiredModal();
      } else {
        setStartExamErrorVariant(
          errorCode === StartExamErrorCode.NotInSession ? 'not-in-exam-time' : 'system-error',
        );
        open();
      }
    } catch (error) {
      setStartExamErrorVariant('system-error');
      open();
      Sentry.captureException(error, {
        tags: {
          feature: 'start-exam',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = new Date().getTime() + timeOffset;
      if (closestExam && currentTime >= closestExam.startedAt.getTime()) {
        setStartedTimeHasReached(true);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [closestExam, timeOffset]);

  return (
    <>
      <StartExamErrorModal show={showErrorModal} onClose={close} variant={startExamErrorVariant} />
      <div className='flex h-full w-full flex-col overflow-auto' data-exam-content>
        <div className='bg-grey-50 flex-grow p-6 text-center'>
          <Card className='mx-auto size-full h-fit max-w-6xl flex-col items-center gap-10'>
            <div className='flex w-full flex-col gap-4'>
              <div className='bg-grey-50 flex w-full items-center justify-start gap-10 rounded-lg p-4'>
                <div className='text-body1 font-semibold'>{user?.displayName}</div>
                <div className='text-body1 text-text-secondary'>
                  {t('instruction.nationalId.withColon')}{' '}
                  <span className='text-text-primary font-semibold'>{user?.idNumber} </span>
                </div>

                <div className='text-body1 text-text-secondary'>
                  {t('instruction.email.withColon')}{' '}
                  <span className='text-text-primary font-semibold'>{user?.email}</span>
                </div>
              </div>
            </div>
            <ExamDetails />
            <div className='flex w-full flex-col gap-6'>
              <ExamGuidelines />
              <div className='w-fit self-center'>
                <div className='flex items-center gap-2'>
                  <label
                    htmlFor='accept'
                    className='inline-flex cursor-pointer items-center space-x-2'
                  >
                    <input
                      type='checkbox'
                      id='accept'
                      checked={checked}
                      className='peer hidden'
                      onChange={() => setChecked(!checked)}
                    />
                    <div className='peer-checked:bg-primary peer-checked:border-primary flex h-[18px] w-[18px] items-center justify-center rounded-xs border-2 border-gray-400'>
                      <svg
                        className='text-white'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='3'
                        viewBox='0 0 24 24'
                      >
                        <path strokeLinecap='square' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                      </svg>
                    </div>
                    <div className='cursor-pointer text-sm'>{t('instruction.userConsent')}</div>
                  </label>
                </div>
              </div>
              <Button
                className='mx-auto w-3xs flex-none font-normal'
                onClick={handleStartExam}
                disabled={!checked || !startedTimeHasReached || loading}
              >
                {loading ? <LoadingSpinner /> : t('instruction.startExam')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

const InstructionRoute = {
  path: PATHS.Exam.Instruction,
  element: <Instruction />,
};

export default InstructionRoute;
